import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { applySEO, breadcrumbLD } from "@/lib/seo";
import { toast } from "sonner";
import { Users, DollarSign, GitBranch, HeartPulse, GraduationCap, Shirt, Wallet, ArrowLeftRight, Ruler, Weight, Calendar } from "lucide-react";
import ContractHub from "@/pages/ContractHub";
import DepthChart from "@/pages/DepthChart";
import InjuryReport from "@/pages/InjuryReport";
import RookieReportCards from "@/pages/RookieReportCards";
import UniformTracker from "@/pages/UniformTracker";
import CapCorner from "@/components/CapCorner";
import ComparePage from "@/pages/ComparePage";

const ROSTER_HEADER = "https://customer-assets.emergentagent.com/job_titans-stadium/artifacts/yv10l9b2_IMG_0433.jpeg";
const POSITION_FILTERS = ["All", "Offense", "Defense", "Special Teams"];
const groupMap = {
  Offense: ["offense"],
  Defense: ["defense"],
  "Special Teams": ["specialTeam", "special team", "specialteams", "Special Teams", "special_team"],
};

export default function Roster() {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All");
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = ["contracts", "depth", "injuries", "rookies", "uniforms", "cap", "compare"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "roster";

  const setTab = (t) => {
    if (t === "roster") setSearchParams({});
    else setSearchParams({ tab: t });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/roster");
        setAthletes(data.athletes || []);
      } catch (e) {
        toast.error("Roster failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // SEO — dynamic per-tab title, description, canonical, OG/Twitter, ItemList JSON-LD.
  useEffect(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://thetitanstandard.com";
    const canonical = `${origin}/roster${tab === "roster" ? "" : `?tab=${tab}`}`;
    const tabTitles = {
      roster: "Tennessee Titans Roster · Players, Positions & Depth",
      depth: "Tennessee Titans Depth Chart · Starters & Reserves",
      compare: "Compare Titans Players · Head-to-Head Career Stats",
      cap: "Titans Cap Central · Salary Cap Tracker",
      injuries: "Titans Injury Report · Weekly Status Updates",
      rookies: "Titans Rookie Report Cards · Draft Class Grades",
      uniforms: "Titans Uniform Tracker · Home, Away & Alternates",
      contracts: "Titans Contract Hub · Deals, Bonuses, Free Agency",
    };
    const tabDescs = {
      roster: "Full Tennessee Titans roster with player profiles, positions, jersey numbers, height, weight and college. Click any player for career stats and season-by-season history.",
      depth: "Live Tennessee Titans depth chart — starters, backups and special teams by position for the 2026 NFL season.",
      compare: "Compare any two Titans (or any NFL player) side by side across every career stat category — passing, rushing, receiving, defense and scoring.",
      cap: "Track the Tennessee Titans salary cap — top hits, dead money, restructures, and cap space heading into the 2026 season.",
      injuries: "Weekly Tennessee Titans injury report with practice status, game designations, and recovery timelines.",
      rookies: "Grades and analysis for the Tennessee Titans 2026 rookie draft class — snap counts, production and outlook.",
      uniforms: "Tennessee Titans uniform tracker — home whites, road navy, alternates and the rumored Oilers throwbacks.",
      contracts: "Every Tennessee Titans contract — years, guaranteed money, cap hits, and cash flow through the deal.",
    };
    const title = `${tabTitles[tab] || tabTitles.roster} | The Titan Standard`;
    const description = tabDescs[tab] || tabDescs.roster;

    const itemListLd = tab === "roster" && athletes.length ? {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${canonical}#roster-list`,
      name: "Tennessee Titans 2026 Active Roster",
      numberOfItems: athletes.length,
      itemListElement: athletes.slice(0, 60).map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${origin}/player/${a.id}`,
        name: a.fullName,
      })),
    } : null;

    const bc = breadcrumbLD([
      { name: "Home", url: "/" },
      { name: "Roster", url: "/roster" },
    ]);

    return applySEO({
      title,
      description,
      url: canonical,
      canonical,
      image: `${origin}/logo512.png`,
      type: "website",
      jsonLd: [itemListLd, bc].filter(Boolean),
    });
  }, [tab, athletes]);

  const filtered = useMemo(() => {
    let r = athletes;
    if (filter !== "All") {
      const keys = groupMap[filter] || [];
      r = r.filter((a) => keys.some((k) => (a.positionGroup || "").toLowerCase().includes(k.toLowerCase())));
    }
    if (query) {
      const q = query.toLowerCase();
      r = r.filter((a) => (a.fullName || "").toLowerCase().includes(q) || (a.position || "").toLowerCase().includes(q));
    }
    return r;
  }, [athletes, filter, query]);

  return (
    <div data-testid="roster-page">
      <div className="relative h-72 overflow-hidden">
        <img
          src={ROSTER_HEADER}
          alt=""
          aria-hidden="true"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-center select-none"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-titans-void via-titans-void/80 to-titans-void/30" />
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-8 h-full flex items-end pb-10">
          <div>
            <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">2026 Roster</div>
            <h1 className="font-display text-5xl md:text-7xl uppercase tracking-wide">The Titans of Tennessee</h1>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-10">
        {/* TABS */}
        <div className="flex gap-2 mb-8 border-b border-[rgba(138,141,143,0.2)]">
          {[
            { k: "roster", l: "Roster", icon: Users },
            { k: "depth", l: "Depth Chart", icon: GitBranch },
            { k: "compare", l: "Compare", icon: ArrowLeftRight },
            { k: "cap", l: "Cap Central", icon: Wallet },
            { k: "injuries", l: "Injuries", icon: HeartPulse },
            { k: "rookies", l: "Rookies", icon: GraduationCap },
            { k: "uniforms", l: "Uniforms", icon: Shirt },
            { k: "contracts", l: "Contract Hub", icon: DollarSign },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`roster-tab-${t.k}`}
              className={`font-cond uppercase tracking-wider text-sm px-5 py-3 inline-flex items-center gap-2 border-b-2 transition-colors -mb-px ${
                tab === t.k ? "border-titans-red text-white" : "border-transparent text-titans-silver hover:text-white"
              }`}
            >
              <t.icon size={14} /> {t.l}
            </button>
          ))}
        </div>

        {tab === "contracts" ? (
          <ContractHub />
        ) : tab === "depth" ? (
          <DepthChart />
        ) : tab === "compare" ? (
          <div data-testid="roster-compare-panel">
            <ComparePage />
          </div>
        ) : tab === "cap" ? (
          <div data-testid="roster-cap-panel">
            <CapCorner variant="wide" />
          </div>
        ) : tab === "injuries" ? (
          <InjuryReport />
        ) : tab === "rookies" ? (
          <RookieReportCards />
        ) : tab === "uniforms" ? (
          <UniformTracker />
        ) : (
          <>
        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between mb-8">
          <div className="flex flex-wrap gap-2">
            {POSITION_FILTERS.map((p) => (
              <button
                key={p}
                data-testid={`roster-filter-${p.toLowerCase().replace(" ", "-")}`}
                onClick={() => setFilter(p)}
                className={`font-cond uppercase tracking-wider text-sm px-5 py-2.5 border transition-colors ${
                  filter === p
                    ? "bg-titans-red border-titans-red text-white"
                    : "border-[rgba(138,141,143,0.3)] text-titans-silver hover:text-white hover:border-white"
                }`}
              >
                {p}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search players or position…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="roster-search"
            className="bg-titans-navy border border-[rgba(138,141,143,0.3)] focus:border-titans-blue focus:outline-none px-5 py-3 text-white font-body w-full md:w-80"
          />
        </div>

        {loading ? (
          <div className="text-titans-silver">Loading roster…</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((a, idx) => (
              <Link
                to={`/player/${a.id}`}
                key={a.id}
                data-testid={`player-card-${idx}`}
                data-reveal
                style={{ "--reveal-i": idx % 4 }}
                className="titans-card player-card hover-rise group overflow-hidden block"
              >
                <div className="player-img-wrap relative h-56">
                  {a.headshot ? (
                    <img src={a.headshot} alt={a.fullName} loading="lazy" decoding="async" className="relative z-[1] w-full h-full object-cover" />
                  ) : (
                    <div className="relative z-[1] w-full h-full flex items-center justify-center text-white/40 font-display text-5xl">
                      {a.position || "—"}
                    </div>
                  )}
                  <div className="absolute top-3 left-3 z-[2] bg-titans-red text-white font-display text-2xl px-2 leading-tight rounded-sm">
                    #{a.jersey || "--"}
                  </div>
                  <div className="absolute bottom-0 inset-x-0 h-16 z-[1] bg-gradient-to-t from-[rgba(12,35,64,0.75)] to-transparent pointer-events-none" />
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-display text-xl uppercase leading-tight text-white group-hover:text-titans-blue transition-colors">{a.fullName}</div>
                    {a.position && (
                      <span className="pos-badge shrink-0 bg-titans-navy text-white font-cond uppercase tracking-wider text-[10px] px-2 py-1 rounded-sm">
                        {a.position}
                      </span>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px] font-cond uppercase tracking-wider text-titans-silver">
                    {a.height && (
                      <span className="inline-flex items-center gap-1.5" data-testid={`player-height-${idx}`}>
                        <Ruler size={11} className="text-titans-blue shrink-0" />{a.height}
                      </span>
                    )}
                    {a.weight && (
                      <span className="inline-flex items-center gap-1.5" data-testid={`player-weight-${idx}`}>
                        <Weight size={11} className="text-titans-blue shrink-0" />{a.weight}
                      </span>
                    )}
                    {a.age && (
                      <span className="inline-flex items-center gap-1.5" data-testid={`player-age-${idx}`}>
                        <Calendar size={11} className="text-titans-blue shrink-0" />Age {a.age}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 truncate" data-testid={`player-college-${idx}`}>
                      <GraduationCap size={11} className="text-titans-blue shrink-0" />
                      <span className="truncate">{a.college || "—"}</span>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
            {filtered.length === 0 && <div className="text-titans-silver">No players match your search.</div>}
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}
